import { memo } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';

export type Outcome = 'Win' | 'Lose' | 'Unknown';

interface OwnOffenseCardProps {
  attackOP: number;
}

/**
 * SF-147 Component 3/4: split out of the old single WarRoomOutcome block — "Your OP" is now its
 * own always-rendered card, independent of isMissingIntel/isImpossible/outcome (fixes pain point
 * a: the single most basic piece of self-information was previously invisible in exactly the case
 * a player most needs it — no SOHD intel yet). Paired with Component 2's Bug fix 2 (the
 * missing-intel fallback composition is the player's full army, not zero) so this card renders a
 * meaningful number in every state, not a correct-looking-but-wrong 0.
 */
export const OwnOffenseCard = memo(function OwnOffenseCard({ attackOP }: OwnOffenseCardProps) {
  return (
    <div
      className="rounded p-3"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          Your OP
        </span>
        <span className="text-lg font-bold" style={{ color: COLORS.positive }}>
          {formatNumber(Math.round(attackOP))}
        </span>
      </div>
    </div>
  );
});

interface WarRoomOutcomeProps {
  outcome: Outcome;
  attackOP: number;
  targetTD: number | null;
  isStale: boolean;
  isMissingIntel: boolean;
  isImpossible: boolean;
  onSendEverything: () => void;
  /**
   * Sticky offset in px, below any page chrome that's also pinned at `top: 0` above this card in
   * the same scrolling document. Defaults to 0 (no chrome above it — DesktopWarRoomShell's case,
   * since `SiblingRow` only ever renders on mobile). MobileWarRoomShell passes `SiblingRow`'s own
   * rendered height (44px `minHeight` + 1px `borderBottom`, both sticky `top: 0`/opaque —
   * `components/shell/SiblingRow.tsx`) — without this, both bars stick to the identical pixel row
   * and `SiblingRow`'s higher `z-index`/opaque background paints over this card instead of the two
   * stacking, defeating Decision 6's "stays visible through further scrolling" acceptance
   * criterion on every War-group sibling route (`/attack` included, per `constants/warNav.ts`).
   */
  stickyTopOffset?: number;
  /**
   * SF-174: default `true` — preserves existing behavior for every current caller/test unchanged.
   * `MobileWarRoomShell` is the only caller that passes `false` — the reorder means Outcome now
   * sits directly below the taller troop-controls card in a single column, so keeping it sticky
   * there would newly pin the verdict bar over the top of that card while scrolling back up to
   * edit rows. `DesktopWarRoomShell`'s 2-column grid isn't reordered by this spec, so its sticky
   * behavior stays exactly as-is (no `sticky` prop passed, default `true`).
   */
  sticky?: boolean;
}

/**
 * SF-147 Component 4/6: the "Outcome card" — binary Win/Lose/Unknown verdict, margin, and a
 * stale-intel flag. Missing-intel and impossible-target are explicit alternate states of THIS
 * card, not separate banners stacked inside it. "Your OP" (OwnOffenseCard, above) and the troop
 * summary (now part of WarRoomTroopControls) no longer live here — this card is the verdict alone.
 *
 * Decision 6: the verdict becomes a sticky bar pinned at the top of the scroll container once the
 * page scrolls past it, rather than relying on a page-level panel reorder (which can't reach the
 * "visible without scrolling" goal from inside this component's own scope — see CLAUDE.md's
 * Legacy Layout.tsx-scrolls-the-document note: `position: sticky; top: 0` is the correct pattern
 * here, not `position: fixed`). Acceptance: at 360×640, the verdict is visible within one scroll
 * of page load and stays visible through further scrolling.
 */
export const WarRoomOutcome = memo(function WarRoomOutcome({
  outcome,
  attackOP,
  targetTD,
  isStale,
  isMissingIntel,
  isImpossible,
  onSendEverything,
  stickyTopOffset = 0,
  sticky = true,
}: WarRoomOutcomeProps) {
  const margin = targetTD !== null ? attackOP - targetTD : null;

  const outcomeColor =
    isMissingIntel || outcome === 'Unknown' ? COLORS.text.muted
    : outcome === 'Win' ? COLORS.positive
    : COLORS.negative;

  const marginColor = isStale ? COLORS.warning : outcomeColor;

  return (
    <div
      data-testid="warroom-verdict-bar"
      className="rounded p-3 space-y-1"
      style={
        sticky
          ? {
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              position: 'sticky',
              top: stickyTopOffset,
              zIndex: 10,
            }
          : {
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              position: 'static',
            }
      }
    >
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
        Outcome
      </div>

      {/* Single aria-live region for the whole decomposed outcome — SF-122 double-announce rule:
          exactly one live region per announced concept, not one per card. */}
      <div aria-live="polite">
        {isMissingIntel ? (
          <div
            className="text-sm rounded px-2 py-1.5"
            style={{ backgroundColor: '#2a0a0a', border: `1px solid #4a1a1a`, color: COLORS.negative }}
          >
            Unknown — no SOHD intel
          </div>
        ) : isImpossible ? (
          <div className="space-y-1.5">
            <div className="text-sm font-bold" style={{ color: COLORS.negative }}>
              Target too strong — you cannot win this fight
            </div>
            <button
              className="text-xs rounded px-2 py-1"
              style={{
                backgroundColor: COLORS.sectionHeader,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.text.secondary,
                minHeight: 44,
              }}
              onClick={onSendEverything}
            >
              Send everything anyway (chip play)
            </button>
          </div>
        ) : (
          <div className="text-lg font-bold" style={{ color: marginColor }}>
            {outcome === 'Win' ? 'Win ✓' : 'Lose ✗'}
            {margin !== null && (
              <span className="text-sm font-normal ml-2">
                ({margin >= 0 ? '+' : ''}{formatNumber(margin)} OP
                {isStale ? ', stale intel' : ' margin'})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
